// css style for react-grid-layout
import {
    Alert,
    AlertDescription,
    AlertTitle,
    Box,
    Button,
    Container,
    Divider,
    HStack,
    Icon,
    IconButton,
    Switch,
    Text,
    useBreakpointValue,
} from "@chakra-ui/react"
import { MagicGroup } from "component/MagicGroup"
import { MotionHStack } from "component/Motion"
import { PerpTabs, TabData } from "component/PerpTabs"
import { SimpleCard } from "component/SimpleCard"
import { TitleMarketDetail } from "component/Title/TitleMarketDetail"
import { TitleBarStats } from "component/TitleBarStats"
import { RouteName } from "constant/route"
import { useBaseAssetSymbolFromRouterParams, useTickerSymbolFromRouterParams } from "hook/useRouterParams"
import { useAtom } from "jotai"
import { atomWithStorage } from "jotai/utils"
import { SmartBackButton } from "layout/MobileHeader/SmartBackButton"
import { EventName } from "module/analytics/types"
import { OutOfSyncWarning } from "module/network/component/OutOfSyncWarning"
import { TradingPanel } from "module/position/component/TradingPanel"
import { ConnectWalletRequired } from "module/wallet/component/ConnectWalletRequired"
import { useEffect, useMemo, useState } from "react"
import useDimensions from "react-cool-dimensions"
import Countdown, { CountdownRenderProps } from "react-countdown"
import { Responsive, WidthProvider } from "react-grid-layout"
import "react-grid-layout/css/styles.css"
import { BiGlasses } from "react-icons/bi"
import { RiCheckLine, RiLayout2Fill } from "react-icons/ri"
import "react-resizable/css/styles.css"
import { useHistory, useLocation } from "react-router-dom"
import { usePositionTaker } from "sdk-react/clearingHouse/usePositionTaker"
import { scrollbarStyle } from "theme/scrollbarStyle"
import { MarketStatisticsContainer } from "../../container/MarketStatisticsContainer"
import { useMarketStrings } from "../../hook/useMarketStrings"
import { EmergencyShutdownWarning } from "../EmergencyShutdownWarning"
import { FundingRateChart } from "./FundingRateChart"
import { MarketStatsPrimary, MarketStatsSecondary } from "./MarketStats"
import { MarketTradeList } from "./MarketTradeList"
import { PriceChart } from "./PriceChart"
import { TradingInfo } from "./TradingInfo"

enum MAIN_BOX_TAB_ID {
    PRICE_CHART = "PRICE_CHART",
    FUNDING_RATE_CHART = "FUNDING_RATE_CHART",
    STATS = "STATS",
}

/**
 * Setting up responsive grid layout
 * 1. Supports layout customization
 * 2. Responsive
 * 3. Supports draggable and resizable
 */

// When using <ResponsiveReactGridLayout> it is suggested you use the HOC WidthProvider
// https://github.com/react-grid-layout/react-grid-layout#providing-grid-width
const ResponsiveGridLayout = WidthProvider(Responsive)
// Basic setup
// Note: this is market page specific, you should not use this layout in other components
const BREAKPOINTS = { base: 0, xs: 375, sm: 500, md: 570, lg: 650, xl: 820, xxl: 1280, xxxl: 1880 }
const COLS = { base: 1, xs: 1, sm: 1, md: 2, lg: 2, xl: 6, xxl: 8, xxxl: 10 }
// Layout for each breakpoints
const LAYOUT_BASE = [
    // total col:1
    { i: "chart", x: 0, y: 0, w: 1, h: 3, static: true },
    { i: "trade-panel", x: 0, y: 3, w: 1, h: 5, static: true },
    { i: "position", x: 0, y: 8, w: 1, h: 5, static: true },
    { i: "market-trades", x: 0, y: 13, w: 1, h: 3, static: true },
]
const LAYOUT_XS = [
    // total col:1
    { i: "chart", x: 0, y: 0, w: 1, h: 3, static: true },
    { i: "trade-panel", x: 0, y: 3, w: 1, h: 5, static: true },
    { i: "position", x: 0, y: 8, w: 1, h: 5, static: true },
    { i: "market-trades", x: 0, y: 13, w: 1, h: 3, static: true },
]
const LAYOUT_SM = [
    // total col:1
    { i: "chart", x: 0, y: 0, w: 1, h: 2, static: true },
    { i: "trade-panel", x: 0, y: 2, w: 1, h: 3, static: true },
    { i: "position", x: 0, y: 5, w: 2, h: 3, static: true },
    { i: "market-trades", x: 0, y: 8, w: 1, h: 2, static: true },
]
const LAYOUT_MD = [
    // total col:1
    { i: "chart", x: 0, y: 0, w: 4, h: 3, minW: 3, maxW: 6, minH: 2, maxH: 4 },
    { i: "trade-panel", x: 4, y: 0, w: 2, h: 3, minH: 3 },
    { i: "position", x: 0, y: 4, w: 4, h: 2, minH: 2 },
    { i: "market-trades", x: 4, y: 4, w: 2, h: 2, minH: 2 },
]
const LAYOUT_LG = [
    // total col:2
    { i: "chart", x: 0, y: 0, w: 2, h: 3, minW: 3, maxW: 6, minH: 2, maxH: 4 },
    { i: "trade-panel", x: 1, y: 3, w: 1, h: 3, minH: 3 },
    { i: "position", x: 0, y: 3, w: 1, h: 3, minH: 3 },
    { i: "market-trades", x: 4, y: 4, w: 2, h: 2, minH: 2 },
]
const LAYOUT_XL = [
    // total col:6
    { i: "chart", x: 0, y: 0, w: 4, h: 3, minW: 3, maxW: 6, minH: 2, maxH: 4 },
    { i: "trade-panel", x: 4, y: 0, w: 2, h: 3, minH: 3 },
    { i: "position", x: 0, y: 4, w: 4, h: 2, minH: 2 },
    { i: "market-trades", x: 4, y: 4, w: 2, h: 2, minH: 2 },
]
const LAYOUT_XXL = [
    // total col:8
    { i: "chart", x: 0, y: 0, w: 6, h: 3, minW: 4, maxW: 8, minH: 2, maxH: 4 },
    { i: "trade-panel", x: 6, y: 0, w: 2, h: 3, minH: 3 },
    { i: "position", x: 0, y: 4, w: 5, h: 2, minH: 2 },
    { i: "market-trades", x: 5, y: 4, w: 3, h: 2, minH: 2 },
]
const LAYOUT_XXXL = [
    // total col:10
    { i: "chart", x: 0, y: 0, w: 8, h: 3, minW: 4, maxW: 10, minH: 2, maxH: 4 },
    { i: "trade-panel", x: 8, y: 0, w: 2, h: 3, minH: 3 },
    { i: "position", x: 0, y: 4, w: 6, h: 2, minH: 2 },
    { i: "market-trades", x: 6, y: 4, w: 4, h: 2, minH: 2 },
]

// Save and load layouts in localStorage
const LAYOUTS_DEFAULT = {
    base: LAYOUT_BASE,
    xs: LAYOUT_XS,
    sm: LAYOUT_SM,
    md: LAYOUT_MD,
    lg: LAYOUT_LG,
    xl: LAYOUT_XL,
    xxl: LAYOUT_XXL,
    xxxl: LAYOUT_XXXL,
}
const LAYOUTS_ORIGINAL = getFromLS("layouts_v1") || LAYOUTS_DEFAULT

function getFromLS(key: string) {
    let ls: any = {}
    if (global.localStorage) {
        try {
            ls = JSON.parse(global.localStorage.getItem("market-detail-layout")!) || {}
        } catch (e) {
            /*Ignore*/
        }
    }
    return ls[key]
}

// TODO: Refactor functions (type any is not good)
function saveToLS(key: any, value: any) {
    if (global.localStorage) {
        global.localStorage.setItem("market-detail-layout", JSON.stringify({ [key]: value }))
    }
}

// global advanced chart settings
const advancedChartAtom = atomWithStorage("isAdvanced", true)
// End of react grid layout settings

export function MarketDetail() {
    const baseSymbol = useBaseAssetSymbolFromRouterParams()
    const tickerSymbol = useTickerSymbolFromRouterParams()

    const position = usePositionTaker(tickerSymbol)

    const { marketPriceStr } = useMarketStrings(tickerSymbol)
    const title = useBaseAssetSymbolFromRouterParams()

    /* ========== START OF REACT GRID LAYOUT STUFF ========== */
    // Load layout from local storage or default value on startup
    const [layouts, setLayouts] = useState(LAYOUTS_ORIGINAL)

    useEffect(() => {
        const loadedLayout = getFromLS("layouts_v1") || LAYOUTS_DEFAULT
        setLayouts(loadedLayout)
    }, [])

    async function resetLayout() {
        await setLayouts(LAYOUTS_DEFAULT)
    }

    async function onLayoutChange(layout: any, layouts: any) {
        await saveToLS("layouts_v1", layouts)
        await setLayouts(layouts)
    }

    // Customize page layout
    const [isDraggable, setIsDraggable] = useState(false)

    const history = useHistory()
    const mainCardPaddingTop = useBreakpointValue({ base: "16px", md: "16px" })

    // Persistent global advanced chart settings
    const [isAdvanced, setIsAdvanced] = useAtom(advancedChartAtom)

    // rowHeight
    const { width, currentBreakpoint, observe } = useDimensions({
        // set up width detection for the container, this must be set up the same way as the grid layout
        breakpoints: BREAKPOINTS,

        // Will only update the state on breakpoint changed, default is false
        // updateOnBreakpointChange: true,
    })

    // the row height is determined by currentBreakpoint (content aware), not by browser width

    // switch statement of breakpoints
    const rowHeight = useMemo(() => {
        switch (currentBreakpoint) {
            case "base":
                return 100
            case "xs":
                return 100
            case "sm":
                return 160
            case "md":
                return 160
            case "lg":
                return 160
            default:
                return 160
        }
    }, [currentBreakpoint])

    // Toolbar breakpoints
    const isCompact = width <= BREAKPOINTS.lg + 20 // < 570
    /* ========== END OF REACT GRID LAYOUT STUFF ========== */

    return (
        <MarketStatisticsContainer.Provider initialState={{ tickerSymbol }}>
            <SmartBackButton onClickBack={() => history.push(RouteName.MARKET_LIST)} />
            <Container
                maxW={{ xl: "1280px", "2xl": "100%" }}
                pt={{ base: 2, md: "40px" }}
                pb={{ base: 128, md: 12 }}
                px={{ base: 0, sm: 6 }}
            >
                <MotionHStack
                    layout
                    spacing={8}
                    mt={{ base: 2, md: 0 }}
                    pl={{ base: "72px", sm: 12, md: 0 }}
                    pr={{ base: 6, sm: 0 }}
                    minH="40px"
                    justify="space-between"
                >
                    <MotionHStack h="44px" spacing={8}>
                        <TitleMarketDetail />

                        <MagicGroup contentWidth="240px" isDisabled={!isCompact} hasSummary={false}>
                            <Box
                                mr={isCompact ? 6 : 0} // NOTE: compensate for magic group
                            >
                                <MarketStatsPrimary tickerSymbol={tickerSymbol} />
                            </Box>
                            <Divider
                                borderColor={isCompact ? undefined : "gray.600"}
                                orientation={isCompact ? "horizontal" : "vertical"}
                            />
                            <MarketStatsSecondary isInline={!isCompact} tickerSymbol={tickerSymbol} />
                        </MagicGroup>
                    </MotionHStack>

                    {/* Layout editing */}
                    {!isCompact && (
                        <HStack>
                            {isDraggable && (
                                <Button size="sm" onClick={() => resetLayout()}>
                                    Reset
                                </Button>
                            )}
                            <IconButton
                                icon={isDraggable ? <RiCheckLine /> : <RiLayout2Fill />}
                                colorScheme={isDraggable ? "perpetual" : "gray"}
                                size="sm"
                                onClick={() => setIsDraggable(!isDraggable)}
                                aria-label={"Customize"}
                            />
                        </HStack>
                    )}
                </MotionHStack>

                <OutOfSyncWarning pt={4} />
                <DelistWarning />
                <EmergencyShutdownWarning tickerSymbol={tickerSymbol} />
                <ResponsiveGridLayout
                    measureBeforeMount={true}
                    style={{ paddingTop: mainCardPaddingTop }}
                    isDraggable={isDraggable}
                    isResizable={isDraggable}
                    resizeHandle={
                        <div
                            className="react-resizable-handle"
                            style={{
                                zIndex: 1,
                                cursor: "nwse-resize",
                                width: 8,
                                height: 8,
                                transform: "scale(1.5)",
                                filter: "invert(1)",
                                position: "absolute",
                                right: 3,
                                bottom: 3,
                            }}
                        />
                    }
                    margin={[24, 24]}
                    className="layout"
                    layouts={layouts}
                    breakpoints={BREAKPOINTS}
                    cols={COLS}
                    rowHeight={rowHeight}
                    containerPadding={[0, 0]}
                    onLayoutChange={(layout, layouts) => {
                        onLayoutChange(layout, layouts)
                    }}
                >
                    {/*
                    Child component requirement: must accepts ref style and className:
                    https://github.com/react-grid-layout/react-grid-layout#custom-child-components-and-draggable-handles
                    Forward refs to an underlying DOM node, and
                    Forward style and className to that same DOM node.
                    Key should be the same as defined in the grid layout

                    Important: And you need to have a {children} in the child element so the rect-grid-layout can insert the resize handle.
                */}
                    <SimpleCard overflow="hidden" key="chart">
                        <PerpTabs
                            tabListProps={{
                                paddingX: 4,
                                paddingTop: 4,
                                minH: "53px",
                                sx: scrollbarStyle,
                            }}
                            baseTabProps={{ h: "29px" }}
                            baseTabPanelProps={{ padding: 0 }}
                            tabs={[
                                {
                                    tabProps: { children: "Price" },
                                    tabPanelProps: {
                                        id: MAIN_BOX_TAB_ID.PRICE_CHART,
                                        children: (
                                            <>
                                                <MotionHStack position={"absolute"} zIndex={1} top={5} right={3}>
                                                    <Icon as={BiGlasses} color="gray.400" boxSize={"20px"} />
                                                    <Switch
                                                        isChecked={isAdvanced}
                                                        onChange={() => {
                                                            setIsAdvanced(!isAdvanced)
                                                        }}
                                                        colorScheme={"perpetual"}
                                                        size="sm"
                                                    />
                                                </MotionHStack>
                                                {isAdvanced ? (
                                                    // NOTE: force reload chart when setting changes
                                                    <PriceChart
                                                        key="advanced"
                                                        isAdvanced={true}
                                                        tickerSymbol={tickerSymbol}
                                                        position={position}
                                                    />
                                                ) : (
                                                    <PriceChart
                                                        key="simple"
                                                        isAdvanced={false}
                                                        tickerSymbol={tickerSymbol}
                                                        position={position}
                                                    />
                                                )}
                                            </>
                                        ),
                                    },
                                },

                                {
                                    tabProps: { children: "Funding" },
                                    tabPanelProps: {
                                        id: MAIN_BOX_TAB_ID.FUNDING_RATE_CHART,
                                        renderContent: ({ isActive }) => (
                                            <FundingRateChart isActive={isActive} tickerSymbol={tickerSymbol} />
                                        ),
                                    },
                                } as TabData,
                            ]}
                            trackingProps={{ eventName: EventName.MARKET_DETAIL_TAB_TOGGLE }}
                        />
                    </SimpleCard>
                    <SimpleCard key="trade-panel">
                        <TradingPanel
                            tickerSymbol={tickerSymbol}
                            baseAssetSymbol={baseSymbol}
                            existingPosition={position}
                        />
                    </SimpleCard>
                    <SimpleCard key="position" py={4}>
                        <ConnectWalletRequired pb={5}>
                            <TradingInfo />
                        </ConnectWalletRequired>
                    </SimpleCard>
                    <SimpleCard key="market-trades">
                        <MarketTradeList key={tickerSymbol + "trades"} listLength={20} tickerSymbol={tickerSymbol} />
                    </SimpleCard>
                </ResponsiveGridLayout>
                <div style={{ width: "100%" }} ref={observe} />
            </Container>
            <TitleBarStats marketPriceStr={marketPriceStr} title={title} />
        </MarketStatisticsContainer.Provider>
    )
}

// TODO: refactor DelistWarning, refactor into reusable components, accepts time, and tickerSymbol.
export function DelistWarning() {
    const location = useLocation()

    const delistDate = new Date("17 Aug 2023 17:00:00 GMT+0800").getTime()

    if (location.pathname === "/markets/SAND:USD" || location.pathname === "/markets/FLOW:USD") {
        return (
            <Countdown
                // date={Date.now() + 30000}
                date={delistDate}
                renderer={renderer}
            />
        )
    } else return null
}

const renderer = ({ completed, days, hours, minutes, seconds }: CountdownRenderProps) => {
    if (!completed) {
        // Render a countdown
        return (
            <Alert
                status="warning"
                variant="subtle"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                py={5}
                mt={6}
                mb={2}
            >
                <AlertTitle mt={4} mb={1} fontSize="lg">
                    <HStack>
                        <Text>
                            This market will be delisted in {days}d {hours}h {minutes}m {seconds}s
                        </Text>
                    </HStack>
                </AlertTitle>
                <AlertDescription>
                    Please close all positions including Trade and Pool positions. Remaining positions will be settled
                    using an index TWAP price at the time of pausing the market.
                </AlertDescription>
            </Alert>
        )
    } else {
        return null
        // Render a completed state
    }
}
